import { action, makeObservable, observable } from "mobx"
import { IMarketplaceRepository } from "../../../domain/repositories/marketplaceRepository"
import { Marketplace, MarketplaceCode } from "../../../domain/entities/Marketplace"
import { ErrorHandler } from "../../error/ErrorHandler"
import { Language } from "../../../locale/interfaces/Translation"
import { GetBannersUseCase } from "../../../domain/useCases/banners/getBanners"
import { SaveBannersUseCase } from "../../../domain/useCases/banners/saveBanners"
import { BannerForm } from "../../components/BannersFormView/entities/BannerForm"
import { LandingReview } from "../../../domain/entities/LandingReview"
import { Tip } from "../../../domain/entities/Tip"
import { GetAllLandingReviewsUseCase } from "../../../domain/useCases/microsites/getAllLandingReviews"
import { GetLandingReviewsBySlugUseCase } from "../../../domain/useCases/microsites/getLandingReviewsBySlug"
import { UpdateLandingReviewsBySlugUseCase } from "../../../domain/useCases/microsites/updateLandingReviewsBySlug"
import { GetTipsUseCase } from "../../../domain/useCases/tips/getTips"
import { AddCountryTipUseCase } from "../../../domain/useCases/tips/addCountryTip"
import { UpdateCountryTipUseCase } from "../../../domain/useCases/tips/updateCountryTip"
import { DeleteCountryTipUseCase } from "../../../domain/useCases/tips/deleteCountryTip"

export interface MarketplaceTipItem {
	tipId: number
	position: number
	tip: Tip
}

interface Props {
	MarketplaceRepository: IMarketplaceRepository
	ErrorHandler: ErrorHandler
	GetBannersUseCase: GetBannersUseCase
	SaveBannersUseCase: SaveBannersUseCase
	GetAllLandingReviewsUseCase: GetAllLandingReviewsUseCase
	GetLandingReviewsBySlugUseCase: GetLandingReviewsBySlugUseCase
	UpdateLandingReviewsBySlugUseCase: UpdateLandingReviewsBySlugUseCase
	GetTipsUseCase: GetTipsUseCase
	AddCountryTipUseCase: AddCountryTipUseCase
	UpdateCountryTipUseCase: UpdateCountryTipUseCase
	DeleteCountryTipUseCase: DeleteCountryTipUseCase
}

export class MarketplacesViewModel {
	private _marketplaceRepository: IMarketplaceRepository
	private _errorHandler: ErrorHandler
	private _getBannersUseCase: GetBannersUseCase
	private _saveBannersUseCase: SaveBannersUseCase
	private _getAllLandingReviewsUseCase: GetAllLandingReviewsUseCase
	private _getLandingReviewsBySlugUseCase: GetLandingReviewsBySlugUseCase
	private _updateLandingReviewsBySlugUseCase: UpdateLandingReviewsBySlugUseCase
	private _getTipsUseCase: GetTipsUseCase
	private _addCountryTipUseCase: AddCountryTipUseCase
	private _updateCountryTipUseCase: UpdateCountryTipUseCase
	private _deleteCountryTipUseCase: DeleteCountryTipUseCase

	isLoading = false
	marketplaces: Marketplace[] = []
	selectedLanguage = "ES"
	bannersEs: BannerForm[] = []
	bannersMx: BannerForm[] = []
	landingReviewsCatalog: LandingReview[] = []
	landingReviewsEs: LandingReview[] = []
	landingReviewsMx: LandingReview[] = []
	tipsCatalog: Tip[] = []
	marketplaceTipsEs: MarketplaceTipItem[] = []
	marketplaceTipsMx: MarketplaceTipItem[] = []
	private _initialMarketplaceTipsEs: MarketplaceTipItem[] = []
	private _initialMarketplaceTipsMx: MarketplaceTipItem[] = []

	constructor({
		MarketplaceRepository,
		ErrorHandler,
		GetBannersUseCase,
		SaveBannersUseCase,
		GetAllLandingReviewsUseCase,
		GetLandingReviewsBySlugUseCase,
		UpdateLandingReviewsBySlugUseCase,
		GetTipsUseCase,
		AddCountryTipUseCase,
		UpdateCountryTipUseCase,
		DeleteCountryTipUseCase
	}: Props) {
		makeObservable(this, {
			isLoading: observable,
			marketplaces: observable,
			selectedLanguage: observable,
			bannersEs: observable,
			bannersMx: observable,
			landingReviewsCatalog: observable,
			landingReviewsEs: observable,
			landingReviewsMx: observable,
			tipsCatalog: observable,
			marketplaceTipsEs: observable,
			marketplaceTipsMx: observable,
			setLoading: action,
			setMarketplaces: action,
			updateMarketplace: action,
			setSelectedLanguage: action,
			setBanners: action,
			setLandingReviewsCatalog: action,
			setLandingReviewsBySlug: action,
			addLandingReviewBySlug: action,
			removeLandingReviewBySlug: action,
			updateLandingReviewWeightBySlug: action,
			setTipsCatalog: action,
			setMarketplaceTipsByCode: action,
			addMarketplaceTipByCode: action,
			removeMarketplaceTipByCode: action,
			updateMarketplaceTipPositionByCode: action
		})
		this._marketplaceRepository = MarketplaceRepository
		this._errorHandler = ErrorHandler
		this._getBannersUseCase = GetBannersUseCase
		this._saveBannersUseCase = SaveBannersUseCase
		this._getAllLandingReviewsUseCase = GetAllLandingReviewsUseCase
		this._getLandingReviewsBySlugUseCase = GetLandingReviewsBySlugUseCase
		this._updateLandingReviewsBySlugUseCase = UpdateLandingReviewsBySlugUseCase
		this._getTipsUseCase = GetTipsUseCase
		this._addCountryTipUseCase = AddCountryTipUseCase
		this._updateCountryTipUseCase = UpdateCountryTipUseCase
		this._deleteCountryTipUseCase = DeleteCountryTipUseCase
		this.loadMarketplaces()
		this.loadBanners()
		this.loadLandingReviewsCatalog()
		this.loadLandingReviews()
		this.loadTipsCatalog()
		this.loadMarketplaceTips()
	}

	private getSlugByMarketplaceCode(code: MarketplaceCode) {
		return code === MarketplaceCode.SPAIN ? "es" : "mx"
	}

	private getCountryByMarketplaceCode(code: MarketplaceCode): "ES" | "MX" {
		return code === MarketplaceCode.SPAIN ? "ES" : "MX"
	}

	private getRegionKey(value: string): "es" | "mx" | undefined {
		const normalized = value.toLowerCase().trim()
		if (normalized === "es" || normalized === MarketplaceCode.SPAIN) return "es"
		if (normalized === "mx" || normalized === MarketplaceCode.MEXICO) return "mx"
		return undefined
	}

	setLoading(value: boolean) {
		this.isLoading = value
	}

	setMarketplaces(marketplaces: Marketplace[]) {
		this.marketplaces = marketplaces
	}

	setSelectedLanguage(language: Language) {
		this.selectedLanguage = language
	}

	setBanners(banners: BannerForm[], region: string) {
		if (region === "ES") this.bannersEs = banners
		else if (region === "MX") this.bannersMx = banners
	}

	setLandingReviewsCatalog(reviews: LandingReview[]) {
		this.landingReviewsCatalog = reviews
	}

	setTipsCatalog(tips: Tip[]) {
		this.tipsCatalog = tips
	}

	setLandingReviewsBySlug(slug: string, reviews: LandingReview[]) {
		const region = this.getRegionKey(slug)
		if (region === "es") this.landingReviewsEs = reviews
		else if (region === "mx") this.landingReviewsMx = reviews
	}

	addLandingReviewBySlug(slug: string, review: LandingReview) {
		const region = this.getRegionKey(slug)
		if (!region) return
		const currentReviews = region === "es" ? this.landingReviewsEs : this.landingReviewsMx
		if (currentReviews.some(currentReview => currentReview.id === review.id)) return

		this.setLandingReviewsBySlug(region, currentReviews.concat(review))
	}

	removeLandingReviewBySlug(slug: string, reviewId: number) {
		const region = this.getRegionKey(slug)
		if (!region) return
		const currentReviews = region === "es" ? this.landingReviewsEs : this.landingReviewsMx
		this.setLandingReviewsBySlug(
			region,
			currentReviews.filter(review => review.id !== reviewId)
		)
	}

	updateLandingReviewWeightBySlug(slug: string, reviewId: number, weight: number) {
		const region = this.getRegionKey(slug)
		if (!region) return
		const currentReviews = region === "es" ? this.landingReviewsEs : this.landingReviewsMx
		this.setLandingReviewsBySlug(
			region,
			currentReviews.map(review =>
				review.id === reviewId
					? {
							...review,
							weight
					  }
					: review
			)
		)
	}

	setMarketplaceTipsByCode(code: MarketplaceCode, tips: MarketplaceTipItem[]) {
		if (code === MarketplaceCode.SPAIN) this.marketplaceTipsEs = tips
		else this.marketplaceTipsMx = tips
	}

	addMarketplaceTipByCode(code: MarketplaceCode, tip: MarketplaceTipItem) {
		const currentTips = code === MarketplaceCode.SPAIN ? this.marketplaceTipsEs : this.marketplaceTipsMx
		if (currentTips.some(item => item.tipId === tip.tipId)) return
		this.setMarketplaceTipsByCode(code, currentTips.concat(tip))
	}

	removeMarketplaceTipByCode(code: MarketplaceCode, tipId: number) {
		const currentTips = code === MarketplaceCode.SPAIN ? this.marketplaceTipsEs : this.marketplaceTipsMx
		this.setMarketplaceTipsByCode(
			code,
			currentTips.filter(item => item.tipId !== tipId)
		)
	}

	updateMarketplaceTipPositionByCode(code: MarketplaceCode, tipId: number, position: number) {
		const currentTips = code === MarketplaceCode.SPAIN ? this.marketplaceTipsEs : this.marketplaceTipsMx
		this.setMarketplaceTipsByCode(
			code,
			currentTips.map(item =>
				item.tipId === tipId
					? {
							...item,
							position
					  }
					: item
			)
		)
	}

	private mapTipsByCountry(country: "ES" | "MX", tips: Tip[]): MarketplaceTipItem[] {
		return tips
			.map((tip, index) => ({
				tipId: tip.id,
				position:
					tip.position ??
					tip.countries?.find(countryTip => countryTip.countryId === country)?.position ??
					index + 1,
				tip
			}))
			.sort((a, b) => a.position - b.position)
	}

	private setInitialMarketplaceTips(code: MarketplaceCode, tips: MarketplaceTipItem[]) {
		if (code === MarketplaceCode.SPAIN) this._initialMarketplaceTipsEs = tips
		else this._initialMarketplaceTipsMx = tips
	}

	updateMarketplace(code: MarketplaceCode, giftcardFee: number | undefined) {
		const index = this.marketplaces.findIndex(m => m.code === code)
		if (index !== -1 && giftcardFee !== undefined) {
			const updatedMarketplaces = [...this.marketplaces]
			updatedMarketplaces[index] = new Marketplace(code, giftcardFee)
			this.marketplaces = updatedMarketplaces
		}
	}

	hasIncompleteBanners() {
		return this.bannersEs.concat(this.bannersMx).some(b => !b.deleted && (!b.desktopImage || !b.mobileImage))
	}

	async loadMarketplaces() {
		try {
			this.setLoading(true)
			const [spain, mexico] = await Promise.all([
				this._marketplaceRepository.getMarketplace(MarketplaceCode.SPAIN),
				this._marketplaceRepository.getMarketplace(MarketplaceCode.MEXICO)
			])
			this.setMarketplaces([spain, mexico])
		} catch (error: any) {
			this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async loadBanners() {
		try {
			this.setLoading(true)
			const [bannersEs, bannersMx] = await Promise.all([
				this._getBannersUseCase.exec("ES"),
				this._getBannersUseCase.exec("MX")
			])
			this.bannersEs = bannersEs.map(b => new BannerForm(b))
			this.bannersMx = bannersMx.map(b => new BannerForm(b))
		} catch (error: any) {
			this.bannersEs = []
			this.bannersMx = []
		} finally {
			this.setLoading(false)
		}
	}

	async loadLandingReviewsCatalog() {
		try {
			const reviews = await this._getAllLandingReviewsUseCase.exec()
			this.setLandingReviewsCatalog(reviews)
		} catch (error: any) {
			this.setLandingReviewsCatalog([])
		}
	}

	async loadLandingReviews() {
		try {
			this.setLoading(true)
			const esSlug = this.getSlugByMarketplaceCode(MarketplaceCode.SPAIN)
			const mxSlug = this.getSlugByMarketplaceCode(MarketplaceCode.MEXICO)
			const [esResult, mxResult] = await Promise.allSettled([
				this._getLandingReviewsBySlugUseCase.exec({ slug: esSlug }),
				this._getLandingReviewsBySlugUseCase.exec({ slug: mxSlug })
			])

			if (esResult.status === "fulfilled") {
				this.setLandingReviewsBySlug(esSlug, esResult.value)
			} else {
				this.setLandingReviewsBySlug(esSlug, [])
			}

			if (mxResult.status === "fulfilled") {
				this.setLandingReviewsBySlug(mxSlug, mxResult.value)
			} else {
				this.setLandingReviewsBySlug(mxSlug, [])
			}
		} catch (error: any) {
			this.setLandingReviewsBySlug("es", [])
			this.setLandingReviewsBySlug("mx", [])
		} finally {
			this.setLoading(false)
		}
	}

	async loadTipsCatalog() {
		try {
			const tips = await this._getTipsUseCase.exec({
				pagination: 0,
				limit: 500
			})
			this.setTipsCatalog(tips)
		} catch (error: any) {
			this.setTipsCatalog([])
		}
	}

	async loadMarketplaceTips() {
		try {
			this.setLoading(true)
			const [esResult, mxResult] = await Promise.allSettled([
				this._getTipsUseCase.exec({
					pagination: 0,
					limit: 500,
					country: "ES"
				}),
				this._getTipsUseCase.exec({
					pagination: 0,
					limit: 500,
					country: "MX"
				})
			])

			if (esResult.status === "fulfilled") {
				const mappedTips = this.mapTipsByCountry("ES", esResult.value)
				this.setMarketplaceTipsByCode(MarketplaceCode.SPAIN, mappedTips)
				this.setInitialMarketplaceTips(MarketplaceCode.SPAIN, mappedTips)
			} else {
				this.setMarketplaceTipsByCode(MarketplaceCode.SPAIN, [])
				this.setInitialMarketplaceTips(MarketplaceCode.SPAIN, [])
			}

			if (mxResult.status === "fulfilled") {
				const mappedTips = this.mapTipsByCountry("MX", mxResult.value)
				this.setMarketplaceTipsByCode(MarketplaceCode.MEXICO, mappedTips)
				this.setInitialMarketplaceTips(MarketplaceCode.MEXICO, mappedTips)
			} else {
				this.setMarketplaceTipsByCode(MarketplaceCode.MEXICO, [])
				this.setInitialMarketplaceTips(MarketplaceCode.MEXICO, [])
			}
		} catch (error: any) {
			this.setMarketplaceTipsByCode(MarketplaceCode.SPAIN, [])
			this.setInitialMarketplaceTips(MarketplaceCode.SPAIN, [])
			this.setMarketplaceTipsByCode(MarketplaceCode.MEXICO, [])
			this.setInitialMarketplaceTips(MarketplaceCode.MEXICO, [])
		} finally {
			this.setLoading(false)
		}
	}

	async loadMarketplaceTipsByCode(code: MarketplaceCode) {
		const country = this.getCountryByMarketplaceCode(code)
		try {
			this.setLoading(true)
			const tips = await this._getTipsUseCase.exec({
				pagination: 0,
				limit: 500,
				country
			})
			const mappedTips = this.mapTipsByCountry(country, tips)
			this.setMarketplaceTipsByCode(code, mappedTips)
			this.setInitialMarketplaceTips(code, mappedTips)
		} catch (error: any) {
			this.setMarketplaceTipsByCode(code, [])
			this.setInitialMarketplaceTips(code, [])
		} finally {
			this.setLoading(false)
		}
	}

	async saveMarketplace(code: MarketplaceCode, giftcardFee: number) {
		try {
			this.setLoading(true)
			await this._marketplaceRepository.updateMarketplace(code, giftcardFee)
			const updated = await this._marketplaceRepository.getMarketplace(code)
			this.updateMarketplace(code, updated.giftcardFee)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async saveBanners() {
		try {
			this.setLoading(true)
			await this._saveBannersUseCase.exec(this.bannersEs.concat(this.bannersMx))
			await this.loadBanners()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async saveLandingReviews(code: MarketplaceCode) {
		const marketplace = code === MarketplaceCode.SPAIN ? "ES" : "MX"
		const slug = this.getSlugByMarketplaceCode(code)
		const reviews = code === MarketplaceCode.SPAIN ? this.landingReviewsEs : this.landingReviewsMx

		try {
			this.setLoading(true)
			await this._marketplaceRepository.updateMarketplaceLandingReviews(marketplace, reviews)
			const updatedReviews = await this._getLandingReviewsBySlugUseCase.exec({ slug })
			this.setLandingReviewsBySlug(slug, updatedReviews)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async saveMarketplaceTips(code: MarketplaceCode) {
		const country = this.getCountryByMarketplaceCode(code)
		const currentTips = code === MarketplaceCode.SPAIN ? this.marketplaceTipsEs : this.marketplaceTipsMx
		const initialTips =
			code === MarketplaceCode.SPAIN ? this._initialMarketplaceTipsEs : this._initialMarketplaceTipsMx

		const currentMap = new Map(currentTips.map(tip => [tip.tipId, tip]))
		const initialMap = new Map(initialTips.map(tip => [tip.tipId, tip]))

		const toCreate = currentTips.filter(tip => !initialMap.has(tip.tipId))
		const toUpdate = currentTips.filter(tip => {
			const initialTip = initialMap.get(tip.tipId)
			return !!initialTip && initialTip.position !== tip.position
		})
		const toDelete = initialTips.filter(tip => !currentMap.has(tip.tipId))

		try {
			this.setLoading(true)

			for (const tip of toCreate) {
				await this._addCountryTipUseCase.exec({
					tipId: tip.tipId,
					countryId: country,
					position: Number(tip.position)
				})
			}

			for (const tip of toUpdate) {
				await this._updateCountryTipUseCase.exec({
					tipId: tip.tipId,
					countryId: country,
					position: Number(tip.position)
				})
			}

			for (const tip of toDelete) {
				await this._deleteCountryTipUseCase.exec({
					tipId: tip.tipId,
					countryId: country
				})
			}

			await this.loadMarketplaceTipsByCode(code)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}
}
